import { gConsts } from '@/globals/gConsts';
import { reportUtils } from '@/features/report/lib/reportUtils';
import { toothUtils } from '@/features/tooth/lib/toothUtils';
import { anotacaoUtils } from '@/features/anotacoes/lib/AnotacaoUtils';
import LabelAnnotation from '../../models/LabelAnnotation';

export default class LabelAnnotationTextBuilder {

    /**
     * @param {LabelAnnotation} labelAnnotation 
     */
    constructor(labelAnnotation) {
        /** @type {LabelAnnotation} */
        this.labelAnnotation = labelAnnotation;
    }

    /**
     * @returns {string}
     */
    build() {
        if (!this.labelAnnotation.tipo || !this.labelAnnotation)
            return '';
        if (this.labelAnnotation.tipo == gConsts.node.report.annotation.types.TOOTH) {
            return this.buildToothAnnotation();
        } else if (this.labelAnnotation.tipo == gConsts.node.report.annotation.types.ANAT_ONLY) {
            return this.buildAnatOnlyAnnotation();
        } else if (this.labelAnnotation.tipo == gConsts.node.report.annotation.types.FIXED) {
            return this.buildFixedAnnotation();
        } else if (this.labelAnnotation.tipo == gConsts.node.report.annotation.types.RX_SIDE) {
            return this.buildRxSideAnnotation();
        } else if (this.labelAnnotation.tipo == gConsts.node.report.annotation.types.RTOOTH) {
            return this.buildRToothAnnotation();
        } else if (this.labelAnnotation.tipo == gConsts.node.report.annotation.types.ANAT_RTOOTH) {
            return this.buildAnatRToothAnnotation();
        } else if (this.labelAnnotation.tipo == gConsts.node.report.annotation.types.PROX_ANAT_RTOOTH) {
            return this.buildProxAnatRToothAnnotation();
        }
        return '';
    }

    buildToothAnnotation() {
        const tooth = this.labelAnnotation.localizacao.dentes[0];
        if (!tooth)
            return '';

        return tooth.text;
    }

    buildAnatOnlyAnnotation() {
        const regioesAnatomicas = this.labelAnnotation.localizacao.regioesAnatomicas;
        if (regioesAnatomicas.length > 0) {
            const listText = reportUtils.getListReportText(regioesAnatomicas.map(regiao => regiao.text));
            const isMultiple = regioesAnatomicas.length > 1;
            let especificacaoText = this._buildEspecificacaoText(this.labelAnnotation.localizacao.especificacao, isMultiple);
            return [especificacaoText, listText].join(' ');
        }
        return '';
    }

    buildFixedAnnotation() {
        const especificacao = this.labelAnnotation.localizacao.especificacao;
        if (gConsts.elem.location.report[especificacao]) {
            return gConsts.elem.location.report[especificacao];
        }
        return '';
    }

    buildRxSideAnnotation() {
        let especificacaoText = this._buildEspecificacaoText(this.labelAnnotation.localizacao.especificacao);
        return especificacaoText;
    }

    buildRToothAnnotation() {
        let dentes = this.labelAnnotation.localizacao.dentes;
        if (dentes.length > 0) {
            dentes = this._sortLocalizacaoDentes(dentes);
            const listText = reportUtils.getListReportText(dentes.map(dente => dente.text));
            const isMultiple = dentes.length > 1;
            let especificacaoText = this._buildEspecificacaoText(this.labelAnnotation.localizacao.especificacao, isMultiple);

            return [especificacaoText, listText].join(' ');
        }
        return '';
    }

    buildAnatRToothAnnotation() {
        let dentes = this.labelAnnotation.localizacao.dentes;
        const regiaoAnatomica = this.labelAnnotation.localizacao.regioesAnatomicas[0];
        if (!regiaoAnatomica)
            return '';

        const isMultiple = dentes.length > 1;

        let finalText = regiaoAnatomica.text;
        let especificacao = this.labelAnnotation.localizacao.especificacao;
        // if (!especificacao && dentes.length > 0)
        //     especificacao = 'situacao__regiao_dentes';
        let especificacaoText = this._buildEspecificacaoText(especificacao, isMultiple);
        if (especificacaoText) {
            finalText += ` ${especificacaoText}`;
        } 
        if (['regiao_palatina'].some(e => especificacao?.endsWith(e)))
            return finalText;
        
        if (anotacaoUtils.especificacaoPermiteDentes(especificacao) && dentes.length > 0) {
            dentes = this._sortLocalizacaoDentes(dentes);
            const listText = reportUtils.getListReportText(dentes.map(dente => dente.text));
            finalText += ` ${listText}`;
        }
        return finalText;
    }

    buildProxAnatRToothAnnotation() {
        let dentes = this.labelAnnotation.localizacao.dentes;
        const regioesAnatomicas = this.labelAnnotation.localizacao.regioesAnatomicas;
        if (dentes.length > 0) {
            dentes = this._sortLocalizacaoDentes(dentes);
            const isMultiple = dentes.length > 1;
            const listText = reportUtils.getListReportText(dentes.map(dente => dente.text));
            let especificacao = this.labelAnnotation.localizacao.especificacao || 'proximo_regiao_dente';
            if (especificacao?.endsWith('regiao_anatomica'))
                especificacao = 'proximo_regiao_dente';
            let especificacaoText = this._buildEspecificacaoText(especificacao, isMultiple);
            if (!especificacaoText)
                especificacaoText = isMultiple ? 'próximo à região dos dentes' : 'próximo à região do dente'

            return [especificacaoText, listText].join(' ');
        } else if (regioesAnatomicas.length > 0) {
            const regioesAnatomicasText = reportUtils.getListReportText(regioesAnatomicas.map(regiao => regiao.text));
            const isMultiple = regioesAnatomicas.length > 1;
            let especificacao = this.labelAnnotation.localizacao.especificacao || 'proximo_regiao_anatomica';
            if (especificacao?.endsWith('regiao_dentes'))
                especificacao = 'proximo_regiao_anatomica';
            let especificacaoText = this._buildEspecificacaoText(especificacao, isMultiple);
            if (!especificacaoText)
                especificacaoText = isMultiple ? 'próximo às regiões anatômicas' : 'próximo à região anatômica'

            return [especificacaoText, regioesAnatomicasText].join(' ');
        }
        return '';
    }

    _buildEspecificacaoText(especificacao, isPlural) {
        const especificacaoReport = reportUtils.getEspecificacaoReport(especificacao);
        if (especificacaoReport && especificacaoReport.posicao == 'antes') {
            return especificacaoReport.texto[isPlural ? 'plural' : 'singular'] || '';
        }
        return '';
    }

    _sortLocalizacaoDentes(dentes) {
        return dentes.sort((a, b) => {
            const aDetails = toothUtils.getToothDetailsByLabelId(a.id);
            const bDetails = toothUtils.getToothDetailsByLabelId(b.id);
            return toothUtils.getToothOrderIndex(aDetails.numero, 'complete') - toothUtils.getToothOrderIndex(bDetails.numero, 'complete');
        });
    }
}